import { useMemo } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';

import { useMetaApiInstance } from '../api';
import { HOST_QUERY_KEY } from '../constants';
import useMegaMenuQuery from './useMegaMenuQuery';
import useServiceAppState from './useServiceAppState';

import type { ServiceMeta } from '../constants';

/**
 * 리전에 따라서 콘솔 전체 메뉴 데이터를 제공하는 훅입니다.
 * @description
 * - Meta Api의 응답값을 react-query로 제공합니다.
 * - SSR을 지원하고 있습니다.
 * - queryKey를 기반으로 revalidate 할 수 있습니다.
 */
const useServiceMetaQuery = () => {
  const {
    state: { region, language },
  } = useServiceAppState();
  const { fetchServiceMeta } = useMetaApiInstance();
  const {
    state: { serviceKeyword },
  } = useServiceAppState();
  const { data: categories } = useMegaMenuQuery();

  const serviceId = useMemo(() => {
    for (const category of categories) {
      const currentService = category.services.find(service => service.keyword === serviceKeyword);
      if (currentService) return currentService.id;
    }
  }, [categories, serviceKeyword]);

  return useSuspenseQuery<ServiceMeta | null>({
    queryKey: [
      HOST_QUERY_KEY.GET_SERVICE_META,
      region ? { serviceId, language, region } : { serviceId, language },
    ],
    queryFn: async () => {
      if (serviceId) {
        const serviceMeta = await fetchServiceMeta({ serviceId, region });
        return serviceMeta;
      }
      return null;
    },
    staleTime: Infinity,
  });
};

export default useServiceMetaQuery;
